// app/components/AdvancedChart/utils/plotSelectedIndicator.ts

import { IChartApi, ISeriesApi, UTCTimestamp, LineSeries, LineStyle } from 'lightweight-charts'
import {
  RSI,
  MACD,
  Stochastic,
  BollingerBands,
  ATR,
  MFI,
  ForceIndex,
  WMA
} from 'technicalindicators'
import { Candle } from '../types'
import { formatPrice } from './priceFormatter'

// Interface for enhanced indicator result
interface IndicatorResult {
  mainSeries: ISeriesApi<'Line'> | null
  additionalSeries: ISeriesApi<'Line'>[]
  alertInfo?: {
    currentValue: number
    alertText: string
    alertColor: string
  }
  alertElement?: HTMLElement
}

// Helper function to create alert tag overlay
function createAlertTag(
  chart: IChartApi,
  paneIndex: number,
  alertInfo: {
    currentValue: number
    alertText: string
    alertColor: string
  }
): HTMLElement {
  const alertElement = document.createElement('div')
  alertElement.style.cssText = `
    position: absolute;
    bottom: 32px;
    left: 8px;
    background: ${alertInfo.alertColor}33;
    // color: #000;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: bold;
    z-index: 1000;
    pointer-events: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    // box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  `
  alertElement.textContent = `${alertInfo.alertText} (${alertInfo.currentValue.toFixed(2)})`
  
  // Get the chart container and add the alert element
  const chartContainer = chart.chartElement()
  if (chartContainer) {
    chartContainer.style.position = 'relative'
    chartContainer.appendChild(alertElement)
  }
  
  return alertElement
}

/**
 * This function plots the selected indicator on the chart with enhanced features.
 * If the indicator should be in its own pane, we create a new pane by providing a pane index.
 * Bollinger Bands and WMA are considered main pane indicators.
 * Enhanced features include reference lines and dynamic alert tags.
 *
 * @param chart - the chart API instance.
 * @param candleData - array of candle data.
 * @param selectedIndicator - the indicator name.
 * @param oldResult - the previous indicator result to be removed.
 *
 * @returns an enhanced indicator result with main series, additional series, and alert info.
 */
export function plotSelectedIndicator(
  chart: IChartApi,
  candleData: Candle[],
  selectedIndicator: string,
  oldResult?: IndicatorResult | null
): IndicatorResult | null {
  // Remove the old series if they exist.
  if (oldResult) {
    try {
      if (oldResult.mainSeries) {
        chart.removeSeries(oldResult.mainSeries)
      }
      oldResult.additionalSeries.forEach(series => {
        chart.removeSeries(series)
      })
      // Remove old alert element
      if (oldResult.alertElement && oldResult.alertElement.parentNode) {
        oldResult.alertElement.parentNode.removeChild(oldResult.alertElement)
      }
    } catch (error) {
      console.warn('Failed to remove old indicator series:', error)
    }
  }

  // Define the list of indicators that should appear in their own pane.
  // We now include ATR, MFI, and ForceIndex along with RSI, MACD, and Stochastic.
  const paneIndicators = ['RSI', 'MACD', 'Stochastic', 'ATR', 'MFI', 'ForceIndex']
  
  // Determine the pane index to be used.
  // If the selected indicator is in the paneIndicators list, create a new pane.
  let paneIndex: number | undefined = undefined
  if (paneIndicators.includes(selectedIndicator)) {
    paneIndex = chart.panes().length // A new pane will be added at the bottom.
  }
  // For BollingerBands and WMA, do not assign a pane index so they remain on the main pane.

  // Create a new line series using the v5 API with price formatting for indicators
  const seriesOptions: any = {
    color: '#ff00ff',
    lineWidth: 2,
    ...(paneIndex !== undefined && {
      priceFormat: {
        type: 'custom' as const,
        formatter: formatPrice
      }
    })
  }
  const newLineSeries = chart.addSeries(LineSeries, seriesOptions, paneIndex)
  const additionalSeries: ISeriesApi<'Line'>[] = []
  let alertInfo: IndicatorResult['alertInfo'] | undefined
  let alertElement: HTMLElement | undefined

  let lineData: { time: UTCTimestamp; value: number }[] = []
  const closes = candleData.map(c => c.close)
  const highs = candleData.map(c => c.high)
  const lows = candleData.map(c => c.low)
  const vols = candleData.map(c => c.volume)

  switch (selectedIndicator) {
    case 'RSI': {
      const period = 14
      const results = RSI.calculate({ values: closes, period })
      if (results && results.length > 0) {
        const offset = candleData.length - results.length
        lineData = results
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
        
        // Add RSI reference lines at 30 and 70
        if (paneIndex !== undefined) {
          // Oversold line at 30
          const oversoldLine = chart.addSeries(LineSeries, {
            color: '#00ff00',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Overbought line at 70
          const overboughtLine = chart.addSeries(LineSeries, {
            color: '#ff0000',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Create reference line data
          const timeRange = lineData.length > 0 ? [
            { time: lineData[0].time, value: 30 },
            { time: lineData[lineData.length - 1].time, value: 30 }
          ] : []
          
          const timeRange70 = lineData.length > 0 ? [
            { time: lineData[0].time, value: 70 },
            { time: lineData[lineData.length - 1].time, value: 70 }
          ] : []
          
          oversoldLine.setData(timeRange)
          overboughtLine.setData(timeRange70)
          
          additionalSeries.push(oversoldLine, overboughtLine)
        }
        
        // Generate alert info and create alert tag based on current RSI value
        if (lineData.length > 0 && paneIndex !== undefined) {
          const currentRSI = lineData[lineData.length - 1].value
          if (currentRSI > 70) {
            alertInfo = {
              currentValue: currentRSI,
              alertText: 'Sell Zone (Overbought)',
              alertColor: '#ff0000'
            }
          } else if (currentRSI < 30) {
            alertInfo = {
              currentValue: currentRSI,
              alertText: 'Buy Zone (Oversold)',
              alertColor: '#00ff00'
            }
          } else {
            alertInfo = {
              currentValue: currentRSI,
              alertText: 'Normal Zone',
              alertColor: '#ffff00'
            }
          }
          
          // Create alert tag in the indicator pane
          if (alertInfo) {
            alertElement = createAlertTag(chart, paneIndex, alertInfo)
          }
        }
      }
      break
    }
    case 'MACD': {
      const macdResults = MACD.calculate({
        fastPeriod: 12,
        slowPeriod: 26,
        signalPeriod: 9,
        SimpleMAOscillator: false,
        SimpleMASignal: false,
        values: closes
      })
      if (macdResults && macdResults.length > 0) {
        const offset = candleData.length - macdResults.length
        lineData = macdResults
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val.MACD ?? 0
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
        
        // Add MACD signal line
        if (paneIndex !== undefined) {
          const signalLine = chart.addSeries(LineSeries, {
            color: '#ffa500',
            lineWidth: 2,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          const signalData = macdResults
            .map((val, i) => {
              const dataIndex = i + offset
              if (dataIndex < candleData.length && dataIndex >= 0) {
                return {
                  time: candleData[dataIndex].time as unknown as UTCTimestamp,
                  value: val.signal ?? 0
                }
              }
              return null
            })
            .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
          
          signalLine.setData(signalData)
          additionalSeries.push(signalLine)
        }
        
        // Generate alert info and create alert tag based on MACD vs Signal
        if (lineData.length > 0 && macdResults.length > 0 && paneIndex !== undefined) {
          const currentMACD = lineData[lineData.length - 1].value
          const currentSignal = macdResults[macdResults.length - 1].signal ?? 0
          
          if (currentMACD > currentSignal) {
            alertInfo = {
              currentValue: currentMACD,
              alertText: 'Buy Signal (Bullish Momentum Building)',
              alertColor: '#00ff00'
            }
          } else {
            alertInfo = {
              currentValue: currentMACD,
              alertText: 'Sell Signal (Bearish Momentum Building)',
              alertColor: '#ff0000'
            }
          }
          
          // Create alert tag in the indicator pane
          if (alertInfo) {
            alertElement = createAlertTag(chart, paneIndex, alertInfo)
          }
        }
      }
      break
    }
    case 'Stochastic': {
      const stoch = Stochastic.calculate({
        high: highs,
        low: lows,
        close: closes,
        period: 14,
        signalPeriod: 3
      })
      if (stoch && stoch.length > 0) {
        const offset = candleData.length - stoch.length
        lineData = stoch
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val.k
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
        
        // Add Stochastic reference lines at 20 and 80
        if (paneIndex !== undefined) {
          // Oversold line at 20
          const oversoldLine = chart.addSeries(LineSeries, {
            color: '#00ff00',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Overbought line at 80
          const overboughtLine = chart.addSeries(LineSeries, {
            color: '#ff0000',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Add %D signal line
          const signalLine = chart.addSeries(LineSeries, {
            color: '#ffa500',
            lineWidth: 2,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Create reference line data
          const timeRange20 = lineData.length > 0 ? [
            { time: lineData[0].time, value: 20 },
            { time: lineData[lineData.length - 1].time, value: 20 }
          ] : []
          
          const timeRange80 = lineData.length > 0 ? [
            { time: lineData[0].time, value: 80 },
            { time: lineData[lineData.length - 1].time, value: 80 }
          ] : []
          
          // Create %D signal data
          const signalData = stoch
            .map((val, i) => {
              const dataIndex = i + offset
              if (dataIndex < candleData.length && dataIndex >= 0) {
                return {
                  time: candleData[dataIndex].time as unknown as UTCTimestamp,
                  value: val.d
                }
              }
              return null
            })
            .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
          
          oversoldLine.setData(timeRange20)
          overboughtLine.setData(timeRange80)
          signalLine.setData(signalData)
          
          additionalSeries.push(oversoldLine, overboughtLine, signalLine)
        }
        
        // Generate alert info based on current Stochastic %K value
        if (lineData.length > 0 && paneIndex !== undefined) {
          const currentK = lineData[lineData.length - 1].value
          if (currentK > 80) {
            alertInfo = {
              currentValue: currentK,
              alertText: 'Sell Zone (Overbought)',
              alertColor: '#ff0000'
            }
          } else if (currentK < 20) {
            alertInfo = {
              currentValue: currentK,
              alertText: 'Buy Zone (Oversold)',
              alertColor: '#00ff00'
            }
          } else {
            alertInfo = {
              currentValue: currentK,
              alertText: 'Normal Zone',
              alertColor: '#ffff00'
            }
          }
          
          // Create alert tag in the indicator pane
          if (alertInfo) {
            alertElement = createAlertTag(chart, paneIndex, alertInfo)
          }
        }
      }
      break
    }
    case 'ATR': {
      const atr = ATR.calculate({
        period: 14,
        high: highs,
        low: lows,
        close: closes
      })
      if (atr && atr.length > 0) {
        const offset = candleData.length - atr.length
        lineData = atr
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
        
        // Generate alert info based on ATR volatility levels
        if (lineData.length > 0 && paneIndex !== undefined) {
          const currentATR = lineData[lineData.length - 1].value
          const avgATR = lineData.slice(-20).reduce((sum, point) => sum + point.value, 0) / Math.min(20, lineData.length)
          
          if (currentATR > avgATR * 1.5) {
            alertInfo = {
              currentValue: currentATR,
              alertText: 'High Volatility',
              alertColor: '#ff0000'
            }
          } else if (currentATR < avgATR * 0.7) {
            alertInfo = {
              currentValue: currentATR,
              alertText: 'Low Volatility',
              alertColor: '#00ff00'
            }
          } else {
            alertInfo = {
              currentValue: currentATR,
              alertText: 'Normal Volatility',
              alertColor: '#ffff00'
            }
          }
          
          // Create alert tag in the indicator pane
          if (alertInfo) {
            alertElement = createAlertTag(chart, paneIndex, alertInfo)
          }
        }
      }
      break
    }
    case 'MFI': {
      const mfi = MFI.calculate({
        high: highs,
        low: lows,
        close: closes,
        volume: vols,
        period: 14
      })
      if (mfi && mfi.length > 0) {
        const offset = candleData.length - mfi.length
        lineData = mfi
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
        
        // Add MFI reference lines at 20 and 80
        if (paneIndex !== undefined) {
          // Oversold line at 20
          const oversoldLine = chart.addSeries(LineSeries, {
            color: '#00ff00',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Overbought line at 80
          const overboughtLine = chart.addSeries(LineSeries, {
            color: '#ff0000',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Create reference line data
          const timeRange20 = lineData.length > 0 ? [
            { time: lineData[0].time, value: 20 },
            { time: lineData[lineData.length - 1].time, value: 20 }
          ] : []
          
          const timeRange80 = lineData.length > 0 ? [
            { time: lineData[0].time, value: 80 },
            { time: lineData[lineData.length - 1].time, value: 80 }
          ] : []
          
          oversoldLine.setData(timeRange20)
          overboughtLine.setData(timeRange80)
          
          additionalSeries.push(oversoldLine, overboughtLine)
        }
        
        // Generate alert info based on current MFI value
        if (lineData.length > 0 && paneIndex !== undefined) {
          const currentMFI = lineData[lineData.length - 1].value
          if (currentMFI > 80) {
            alertInfo = {
              currentValue: currentMFI,
              alertText: 'Sell Zone (Overbought)',
              alertColor: '#ff0000'
            }
          } else if (currentMFI < 20) {
            alertInfo = {
              currentValue: currentMFI,
              alertText: 'Buy Zone (Oversold)',
              alertColor: '#00ff00'
            }
          } else {
            alertInfo = {
              currentValue: currentMFI,
              alertText: 'Normal Zone',
              alertColor: '#ffff00'
            }
          }
          
          // Create alert tag in the indicator pane
          if (alertInfo) {
            alertElement = createAlertTag(chart, paneIndex, alertInfo)
          }
        }
      }
      break
    }
    case 'ForceIndex': {
      const fi = ForceIndex.calculate({
        close: closes,
        volume: vols,
        period: 1
      })
      if (fi && fi.length > 0) {
        const offset = candleData.length - fi.length
        lineData = fi
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
        
        // Add Force Index zero line reference
        if (paneIndex !== undefined) {
          const zeroLine = chart.addSeries(LineSeries, {
            color: '#808080',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            priceFormat: { type: 'custom' as const, formatter: formatPrice }
          }, paneIndex)
          
          // Create zero line data
          const zeroLineData = lineData.length > 0 ? [
            { time: lineData[0].time, value: 0 },
            { time: lineData[lineData.length - 1].time, value: 0 }
          ] : []
          
          zeroLine.setData(zeroLineData)
          additionalSeries.push(zeroLine)
        }
        
        // Generate alert info based on Force Index momentum
        if (lineData.length > 0 && paneIndex !== undefined) {
          const currentFI = lineData[lineData.length - 1].value
          const prevFI = lineData.length > 1 ? lineData[lineData.length - 2].value : 0
          
          if (currentFI > 0 && prevFI <= 0) {
            alertInfo = {
              currentValue: currentFI,
              alertText: 'Buy Signal (Bullish Force)',
              alertColor: '#00ff00'
            }
          } else if (currentFI < 0 && prevFI >= 0) {
            alertInfo = {
              currentValue: currentFI,
              alertText: 'Sell Signal (Bearish Force)',
              alertColor: '#ff0000'
            }
          } else if (currentFI > 0) {
            alertInfo = {
              currentValue: currentFI,
              alertText: 'Bullish Momentum',
              alertColor: '#90EE90'
            }
          } else {
            alertInfo = {
              currentValue: currentFI,
              alertText: 'Bearish Momentum',
              alertColor: '#FFB6C1'
            }
          }
          
          // Create alert tag in the indicator pane
          if (alertInfo) {
            alertElement = createAlertTag(chart, paneIndex, alertInfo)
          }
        }
      }
      break
    }
    case 'BollingerBands': {
      const bb = BollingerBands.calculate({
        period: 20,
        values: closes,
        stdDev: 2
      })
      if (bb && bb.length > 0) {
        const offset = candleData.length - bb.length
        // Plot the 'middle' band on the main pane.
        lineData = bb
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val.middle
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
      }
      break
    }
    case 'WMA': {
      const wma = WMA.calculate({ period: 9, values: closes })
      if (wma && wma.length > 0) {
        const offset = candleData.length - wma.length
        // Plot WMA on the main pane.
        lineData = wma
          .map((val, i) => {
            const dataIndex = i + offset
            if (dataIndex < candleData.length && dataIndex >= 0) {
              return {
                time: candleData[dataIndex].time as unknown as UTCTimestamp,
                value: val
              }
            }
            return null
          })
          .filter((pt): pt is { time: UTCTimestamp; value: number } => pt !== null)
      }
      break
    }
    default:
      console.warn('Selected indicator is not implemented:', selectedIndicator)
      break
  }

  if (lineData.length > 0) {
    newLineSeries.setData(lineData)
  }

  return {
    mainSeries: newLineSeries,
    additionalSeries,
    alertInfo,
    alertElement
  }
}
